import { useCallback, useEffect, useId, useState } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import clsx from 'clsx';

import { Input } from '../Input';
import { Label } from '../Label';

import styles from './styles/Slider.module.scss';

export type SliderProps = {
  label: string;
  shouldShowBackground?: boolean;
  shouldShowLabel?: boolean;
  size?: 's' | 'm';
  unit?: string;
  withInputs?: boolean;
} & RadixSlider.SliderProps;

export const Slider = ({
  className,
  label,
  shouldShowBackground = false,
  shouldShowLabel = false,
  size = 'm',
  unit,
  withInputs = false,
  ...props
}: SliderProps) => {
  const { max = Infinity, min = -Infinity, onValueChange } = props;
  const [sliderValue, setSliderValue] = useState(props.value || props.defaultValue || [0]);
  const [inputValues, setInputValues] = useState<string[]>([]);

  useEffect(() => {
    if (props.value) {
      setSliderValue(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    setInputValues(sliderValue.map(v => String(v)));
  }, [sliderValue]);

  const hasNoValue = sliderValue && sliderValue.length <= 1 && sliderValue[0] === 0;

  const id = useId();
  // Own id, not the literal "output": two sliders on one page both claimed it, so `aria-describedby`
  // resolved to whichever rendered first.
  const outputId = `${id}-output`;

  const handleInputValueChange = useCallback(
    (index: number, value: string) => {
      setInputValues(current => {
        const newInputs = [...current];
        newInputs[index] = value;
        return newInputs;
      });

      const parsedValue = parseInt(value, 10);
      if (isNaN(parsedValue)) {
        return;
      }

      const { max = Infinity, min = -Infinity } = props;
      const clampedValue = Math.max(min, Math.min(parsedValue, max));

      if ((index === 0 && clampedValue > sliderValue[1]) || (index === 1 && clampedValue < sliderValue[0])) {
        return;
      }

      const newSliderValue = [...sliderValue];
      newSliderValue[index] = clampedValue;

      if (newSliderValue.some((v, i) => v !== sliderValue[i])) {
        setSliderValue(newSliderValue);
        props.onValueChange?.(newSliderValue);
      }
    },
    [min, max, onValueChange, props, sliderValue],
  );

  const handleInputBlur = useCallback(
    (index: number) => {
      const parsedValue = parseInt(inputValues[index], 10);
      const oppositeThumbValue = sliderValue[index === 0 ? 1 : 0];

      const isParsedValueNaN = isNaN(parsedValue);
      const isBelowMin = parsedValue < (min ?? -Infinity);
      const isAboveMax = parsedValue > (max ?? Infinity);
      const isCrossingOtherThumb =
        (index === 0 && parsedValue > oppositeThumbValue) || (index === 1 && parsedValue < oppositeThumbValue);

      const isInvalid = isParsedValueNaN || isBelowMin || isAboveMax || isCrossingOtherThumb;

      if (isInvalid) {
        setInputValues(current => {
          const newInputs = [...current];
          newInputs[index] = String(sliderValue[index]);
          return newInputs;
        });
      } else {
        handleInputValueChange(index, String(parsedValue));
      }
    },
    [inputValues, sliderValue, min, max, handleInputValueChange],
  );

  const handleSliderChange = useCallback(
    (newValues: number[]) => {
      setSliderValue(newValues);
      onValueChange?.(newValues);
    },
    [onValueChange],
  );

  return (
    <div className={clsx(styles.sliderWrapper, styles[`size-${size}`])} data-cxy-name="slider">
      {shouldShowLabel && (
        <div className={styles.labelContainer}>
          <Label className={styles.label} htmlFor={id} size="s">
            {label}
          </Label>
        </div>
      )}
      <div className={styles.controlsWrapper}>
        <div
          className={clsx(styles.sliderContainer, {
            [styles.shouldShowBackground]: shouldShowBackground,
          })}
        >
          <RadixSlider.Root
            id={id}
            {...(!shouldShowLabel && { 'aria-label': label })}
            aria-describedby={outputId}
            className={clsx(styles.slider, className)}
            {...props}
            onValueChange={handleSliderChange}
            value={sliderValue}
          >
            <RadixSlider.Track className={styles.track}>
              <RadixSlider.Range className={styles.range} />
            </RadixSlider.Track>
            {sliderValue.map((_, index) => (
              <RadixSlider.Thumb
                {...(!shouldShowLabel && { 'aria-label': label })}
                className={clsx(styles.thumb, { [styles.noValue]: hasNoValue })}
                key={`${label}-thumb-${index}`}
              />
            ))}
          </RadixSlider.Root>
          {!withInputs && (
            <p className={styles.output} data-testid="slider-output" id={outputId}>
              {sliderValue.map((_, index) => (
                <span key={`${label}-output-${index}`}>
                  {`${sliderValue[index]}${index === sliderValue.length - 1 ? '' : '-'}`}
                </span>
              ))}
              {unit ? unit : ''}
            </p>
          )}
        </div>
        {withInputs && sliderValue.length === 2 && (
          <div className={styles.inputsContainer}>
            <div className={styles.inputWrapper}>
              <Input
                className={styles.sliderInput}
                disabled={props.disabled}
                max={sliderValue[1]}
                min={min}
                onBlur={() => handleInputBlur(0)}
                onChange={e => handleInputValueChange(0, e.target.value)}
                type="number"
                value={inputValues[0] ?? ''}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Input
                className={styles.sliderInput}
                disabled={props.disabled}
                max={max}
                min={sliderValue[0]}
                onBlur={() => handleInputBlur(1)}
                onChange={e => handleInputValueChange(1, e.target.value)}
                type="number"
                value={inputValues[1] ?? ''}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
